import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileCsv, MagnifyingGlass } from "@phosphor-icons/react";
import { downloadCSV } from "@/lib/reportExport";
import { monthLabel } from "@/lib/payslip";
import { useAuth } from "@/contexts/AuthContext";

const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);

const COLS = [
  { key: "employee_name", label: "Employee" },
  { key: "employee_code", label: "Emp ID" },
  { key: "designation", label: "Designation" },
  { key: "store", label: "Store" },
  { key: "total_days", label: "Days in Month", num: true },
  { key: "present_days", label: "Present", num: true },
  { key: "full_days", label: "Full Days", num: true },
  { key: "half_days", label: "Half Days", num: true },
  { key: "absent_marked", label: "Absent (Late)", num: true },
  { key: "working_days", label: "Working Days", num: true },
  { key: "paid_leave_days", label: "Paid Leave", num: true },
  { key: "other_leave_days", label: "Other Leave", num: true },
  { key: "payable_days", label: "Payable Days", num: true },
  { key: "late_checkin_days", label: "Late Check-in Days", num: true },
  { key: "early_checkout_days", label: "Early Check-out Days", num: true },
  { key: "late_checkout_days", label: "Late Check-out Days", num: true },
  { key: "missed_checkout_days", label: "Missed Check-out", num: true },
  { key: "total_hours", label: "Total Hours", num: true },
  { key: "monthly_salary", label: "Monthly Salary", money: true },
  { key: "advance", label: "Advance", money: true },
  { key: "final_payable", label: "Net Payable", money: true },
  { key: "payment_status", label: "Salary Status" },
];

const cell = (r, c) => {
  const v = r[c.key];
  if (c.money) return v == null ? "—" : inr(v);
  if (c.key === "payment_status") return v ? v.charAt(0).toUpperCase() + v.slice(1) : "Not generated";
  return v === "" || v == null ? "—" : v;
};

const TOTAL_KEYS = COLS.filter((c) => c.num || c.money).map((c) => c.key);

export const HrmsReport = () => {
  const { stores } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    (async () => { try { setData((await api.get(`/hrms/report?month=${month}`)).data); } catch (e) { toast.error(formatApiError(e)); } })();
  }, [month]);
  const storeName = useMemo(() => Object.fromEntries((stores || []).map((s) => [s.id, s.name])), [stores]);
  const rows = useMemo(() => (data?.rows || [])
    .map((r) => ({ ...r, store: storeName[r.store_id] || "" }))
    .filter((r) => !q || `${r.employee_name} ${r.employee_code} ${r.designation} ${r.store}`.toLowerCase().includes(q.toLowerCase())), [data, q, storeName]);
  const totals = useMemo(() => Object.fromEntries(TOTAL_KEYS.map((k) => [k, Math.round(rows.reduce((a, r) => a + (Number(r[k]) || 0), 0) * 100) / 100])), [rows]);
  const exportCsv = () => downloadCSV(`HRMS-Report-${month}`, COLS.map((c) => ({ key: c.key, label: c.label, get: (r) => (c.money ? r[c.key] ?? "" : r[c.key]) })), rows);
  const t = data?.timing;
  return (
    <div className="space-y-3" data-testid="hrms-report">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} className="w-44 h-9 font-mono" data-testid="hrms-report-month" />
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee, ID, store" className="w-64 h-9 pl-8" data-testid="hrms-report-search" />
        </div>
        <Button size="sm" variant="outline" className="h-9" onClick={exportCsv} disabled={!rows.length} data-testid="hrms-report-export"><FileCsv size={15} className="mr-1.5" />Excel</Button>
        {t && <span className="text-[11.5px] text-muted-foreground">{monthLabel(month)} · Check-in {t.check_in_time} (grace {t.grace_minutes} min) · Check-out {t.check_out_time}</span>}
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="data-table text-[12px]" data-testid="hrms-report-table">
          <thead><tr>{COLS.map((c) => <th key={c.key} className={c.num || c.money ? "num whitespace-nowrap" : "whitespace-nowrap"}>{c.label}</th>)}</tr></thead>
          <tbody>
            {!data && <tr><td colSpan={COLS.length} className="text-center py-6 text-muted-foreground">Loading…</td></tr>}
            {data && rows.length === 0 && <tr><td colSpan={COLS.length} className="text-center py-6 text-muted-foreground">No employees.</td></tr>}
            {rows.map((r) => (
              <tr key={r.employee_id} data-testid={`hrms-report-row-${r.employee_id}`}>
                {COLS.map((c) => <td key={c.key} className={c.num || c.money ? "num font-mono" : c.key === "employee_name" ? "font-medium whitespace-nowrap" : "whitespace-nowrap"}>{cell(r, c)}</td>)}
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot><tr className="font-semibold" data-testid="hrms-report-totals">
              {COLS.map((c, i) => <td key={c.key} className={c.num || c.money ? "num font-mono" : ""}>{i === 0 ? `Total (${rows.length})` : TOTAL_KEYS.includes(c.key) && c.key !== "total_days" ? (c.money ? inr(totals[c.key]) : totals[c.key]) : ""}</td>)}
            </tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
};
